import React, { useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  CurrencyDollarIcon,
  CalculatorIcon,
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  QuestionMarkCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import useAuthStore from '../store/authStore';
import CommissionExplanationModal from '../components/CommissionExplanationModal';

const ContractorCommission = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [calculatorData, setCalculatorData] = useState({
    construction_types: [''],
    amounts: ['']
  });
  const [calculationResult, setCalculationResult] = useState(null);
  const [showExplanationModal, setShowExplanationModal] = useState(false);
  const [selectedConstructionType, setSelectedConstructionType] = useState(null);
  const [showCommissionRates, setShowCommissionRates] = useState(false);

  // 수수료 내역 조회
  const { data: commissionHistory, isLoading: historyLoading, refetch } = useQuery(
    ['contractor-commission-history', user?.id],
    () => api.get('/contractors/commission-history').then(res => res.data),
    {
      enabled: user?.user_type === 'contractor'
    }
  );

  const feeType = commissionHistory?.fee_type || 'commission';
  const monthlyAmount = commissionHistory?.monthly_amount;
  const paymentDay = commissionHistory?.payment_day;
  const accountInfo = commissionHistory?.account_info;

  // 수수료율 정보 조회
  const { data: commissionRatesData } = useQuery(
    'commission-rates-info',
    () => api.get('/contractors/commission-rates').then(res => res.data)
  );

  const commissionRates = commissionRatesData?.rates || {};
  const availableTypes = commissionRatesData?.availableTypes || [];

  // 입금 완료 신고 mutation
  const paymentRequestMutation = useMutation(
    (commissionId) => api.post('/contractors/commission-payment-request', { commissionId }),
    {
      onSuccess: () => {
        toast.success('입금 완료 신고가 접수되었습니다.');
        refetch();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '입금 완료 신고에 실패했습니다.');
      }
    }
  );

  // 입금 신고 취소 mutation
  const paymentCancelMutation = useMutation(
    (commissionId) => api.post('/contractors/commission-payment-cancel', { commissionId }),
    {
      onSuccess: () => {
        toast.success('입금 신고가 취소되었습니다.');
        refetch();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '입금 신고 취소에 실패했습니다.');
      }
    }
  );

  const formatCurrency = (amount) => {
    if (!amount) return '0원';
    const billion = Math.floor(amount / 100000000);
    const million = Math.floor((amount % 100000000) / 10000);
    
    if (billion > 0) {
      return `${billion}억${million > 0 ? ` ${million}만원` : '원'}`;
    } else if (million > 0) {
      return `${million}만원`;
    } else {
      return new Intl.NumberFormat('ko-KR').format(amount) + '원';
    }
  };

  const handleCalculatorChange = (index, field, value) => {
    const newData = { ...calculatorData };
    newData[field][index] = value;
    setCalculatorData(newData);
  };

  const addCalculatorRow = () => {
    setCalculatorData({
      construction_types: [...calculatorData.construction_types, ''],
      amounts: [...calculatorData.amounts, '']
    });
  };

  const removeCalculatorRow = (index) => {
    const newData = {
      construction_types: calculatorData.construction_types.filter((_, i) => i !== index),
      amounts: calculatorData.amounts.filter((_, i) => i !== index)
    };
    setCalculatorData(newData);
  };

  const calculateCommission = async () => {
    try {
      const validTypes = calculatorData.construction_types.filter((type, index) => 
        type && calculatorData.amounts[index] && parseFloat(calculatorData.amounts[index]) > 0
      );
      const validAmounts = calculatorData.amounts.filter((amount, index) => 
        calculatorData.construction_types[index] && amount && parseFloat(amount) > 0
      ).map(amount => parseFloat(amount));

      if (validTypes.length === 0) {
        alert('계산할 공사 유형과 금액을 입력해주세요.');
        return;
      }

      const response = await api.post('/contractors/commission-calculator', {
        construction_types: validTypes,
        amounts: validAmounts
      });

      setCalculationResult(response.data);
    } catch (error) {
      console.error('Error calculating commission:', error);
      alert('수수료 계산에 실패했습니다.');
    }
  };

  const getStatusBadge = (status, paymentRequestedAt, paymentConfirmedAt) => {
    if (paymentConfirmedAt) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">납부완료</span>;
    } else if (paymentRequestedAt) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">확인대기</span>;
    } else if (status === 'calculated') {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">납부필요</span>;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 */}
      <div className="bg-gradient-to-b from-purple-600 to-purple-700 text-white pb-32">
        <div className="px-4 pt-4 pb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <CurrencyDollarIcon className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">수수료 관리</h1>
                <p className="text-purple-100">
                  {feeType === 'monthly' ? '월정액 수수료' : '프로젝트별 수수료'}
                </p>
              </div>
            </div>
            {feeType === 'commission' && (
              <button
                onClick={() => setShowExplanationModal(true)}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              >
                <QuestionMarkCircleIcon className="h-6 w-6 text-purple-200" />
              </button>
            )}
          </div>

          {/* 주요 통계 */}
          {feeType === 'monthly' ? (
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4">
                <p className="text-purple-100 text-sm mb-1">월 납부액</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(monthlyAmount)}
                </p>
                <p className="text-xs text-purple-200 mt-1">매월 {paymentDay}일</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4">
                <p className="text-purple-100 text-sm mb-1">올해 납부</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(commissionHistory?.total_commission || 0)}
                </p>
                <p className="text-xs text-purple-200 mt-1">
                  {commissionHistory?.paid_count || 0}개월 완료
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4">
                <p className="text-purple-100 text-sm mb-1">이번 달 납부액</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(commissionHistory?.monthly_total || 0)}
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4">
                <p className="text-purple-100 text-sm mb-1">총 납부액</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(commissionHistory?.total_commission || 0)}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="px-4 -mt-20 space-y-4 pb-20">
        {/* 수수료 현황 카드 */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <ChartBarIcon className="h-5 w-5 text-purple-600" />
            납부 현황
          </h2>
          
          {feeType === 'commission' && (
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-2xl p-4 text-center">
                <ExclamationCircleIcon className="h-8 w-8 text-red-500 mx-auto mb-2" />
                <p className="text-sm text-gray-600">납부 필요</p>
                <p className="text-2xl font-bold text-red-600">{commissionHistory?.calculated_count || 0}</p>
              </div>
              <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-2xl p-4 text-center">
                <ClockIcon className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
                <p className="text-sm text-gray-600">확인 대기</p>
                <p className="text-2xl font-bold text-yellow-600">{commissionHistory?.pending_count || 0}</p>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-4 text-center">
                <CheckCircleIcon className="h-8 w-8 text-green-500 mx-auto mb-2" />
                <p className="text-sm text-gray-600">납부 완료</p>
                <p className="text-2xl font-bold text-green-600">{commissionHistory?.paid_count || 0}</p>
              </div>
            </div>
          )}

          {feeType === 'monthly' && (
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-700">월 정액 납부 현황</h3>
                <span className="text-xs text-gray-500">올해</span>
              </div>
              
              <div className="flex items-baseline gap-2 mb-3">
                <span className="text-3xl font-bold text-purple-600">
                  {commissionHistory?.paid_count || 0}개월
                </span>
                <span className="text-sm text-gray-600">/ 12개월</span>
              </div>
              
              <div className="w-full bg-purple-100 rounded-full h-2 mb-3">
                <div 
                  className="bg-gradient-to-r from-purple-500 to-purple-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${((commissionHistory?.paid_count || 0) / 12) * 100}%` }}
                />
              </div>
              
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">납부 금액</span>
                <span className="font-medium text-purple-600">
                  {formatCurrency(commissionHistory?.total_commission || 0)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* 수수료율 표 - 프로젝트별 수수료인 경우만 표시 */}
        {feeType === 'commission' && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <button
              onClick={() => setShowCommissionRates(!showCommissionRates)}
              className="w-full flex items-center justify-between group"
            >
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <InformationCircleIcon className="h-5 w-5 text-purple-600" />
                수수료율 안내
              </h2>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 group-hover:text-purple-600 transition-colors">
                  {showCommissionRates ? '접기' : '펼치기'}
                </span>
                {showCommissionRates ? (
                  <ChevronUpIcon className="h-5 w-5 text-gray-400 group-hover:text-purple-600 transition-colors" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5 text-gray-400 group-hover:text-purple-600 transition-colors" />
                )}
              </div>
            </button>
            
            {/* 아코디언 콘텐츠 */}
            <div className={`transition-all duration-300 overflow-hidden ${showCommissionRates ? 'max-h-[2000px] opacity-100 mt-4' : 'max-h-0 opacity-0'}`}>
              <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 pr-4">공사 유형</th>
                      <th className="text-right px-2">최소 금액</th>
                      <th className="text-right px-2">수수료율</th>
                      <th className="text-center pl-2 w-20">안내</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(commissionRates).map(([type, rates]) => (
                      rates.map((rate, index) => (
                        <tr key={`${type}-${index}`} className="border-b">
                          {index === 0 && (
                            <td rowSpan={rates.length} className="py-2 pr-4 font-medium">
                              {type}
                            </td>
                          )}
                          <td className="text-right px-2 py-2">
                            {formatCurrency(rate.min_amount)} 이상
                          </td>
                          <td className="text-right px-2 py-2 font-medium text-purple-600">
                            {rate.rate_percentage}%
                          </td>
                          {index === 0 && (
                            <td rowSpan={rates.length} className="text-center pl-2">
                              <button
                                onClick={() => {
                                  setSelectedConstructionType(type);
                                  setShowExplanationModal(true);
                                }}
                                className="p-1 hover:bg-gray-100 rounded"
                              >
                                <QuestionMarkCircleIcon className="h-4 w-4 text-gray-500" />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* 추가 안내 문구 */}
              <div className="mt-4 p-3 bg-purple-50 rounded-lg">
                <p className="text-xs text-purple-700">
                  <span className="font-medium">💡 안내:</span> 계약 금액에 따라 구간별로 다른 수수료율이 적용됩니다.
                  자세한 계산 방식은 우측 상단 <QuestionMarkCircleIcon className="inline h-3 w-3" /> 버튼을 클릭해주세요.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* 수수료 계산기 - 프로젝트별 수수료인 경우만 표시 */}
        {feeType === 'commission' && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <CalculatorIcon className="h-5 w-5 text-purple-600" />
            수수료 계산기
          </h2>

          <div className="space-y-3">
            {calculatorData.construction_types.map((type, index) => (
              <div key={index} className="flex gap-3 items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    공사 유형
                  </label>
                  <select
                    value={type}
                    onChange={(e) => handleCalculatorChange(index, 'construction_types', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">선택하세요</option>
                    {availableTypes.map((constructionType) => (
                      <option key={constructionType} value={constructionType}>{constructionType}</option>
                    ))}
                  </select>
                </div>
                
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    계약 금액 (원)
                  </label>
                  <input
                    type="number"
                    value={calculatorData.amounts[index]}
                    onChange={(e) => handleCalculatorChange(index, 'amounts', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0"
                    min="0"
                  />
                </div>

                {calculatorData.construction_types.length > 1 && (
                  <button
                    onClick={() => removeCalculatorRow(index)}
                    className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    삭제
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="flex gap-2 mt-4">
            <button
              onClick={addCalculatorRow}
              className="px-4 py-2 text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
            >
              + 공사 유형 추가
            </button>
            <button
              onClick={calculateCommission}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              수수료 계산
            </button>
          </div>

          {/* 계산 결과 */}
          {calculationResult && (
            <div className="mt-6 p-4 bg-gray-50 rounded-xl">
              <h3 className="text-lg font-bold text-gray-900 mb-4">계산 결과</h3>
              
              <div className="bg-green-100 rounded-lg p-4 mb-4">
                <p className="text-sm text-green-800 mb-1">총 수수료</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(calculationResult.total_commission)}
                </p>
              </div>

              <div className="space-y-3">
                {calculationResult.calculations.map((calc, index) => (
                  <div key={index} className="bg-white rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-gray-900">{calc.construction_type}</span>
                      <span className="text-lg font-bold text-purple-600">
                        {formatCurrency(calc.commission)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      계약금액: {formatCurrency(calc.amount)}
                    </p>
                    
                    {Array.isArray(calc.details) && calc.details.length > 0 && (
                      <div className="space-y-1">
                        {calc.details.map((detail, detailIndex) => (
                          <div key={detailIndex} className="text-xs text-gray-500 flex justify-between">
                            <span>{detail.range}</span>
                            <span>{detail.rate}% = {formatCurrency(detail.commission)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        )}

        {/* 수수료 내역 */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <CurrencyDollarIcon className="h-5 w-5 text-purple-600" />
            수수료 내역
          </h2>

          {historyLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
            </div>
          ) : commissionHistory?.history?.length > 0 ? (
            <div className="space-y-3">
              {commissionHistory.history.map((item) => (
                <div key={item.id} className="border border-purple-100 rounded-xl p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-2 gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 truncate pr-2">
                        {item.project_id ? item.project_title : `${new Date(item.calculated_at).getMonth() + 1}월 정액 수수료`}
                      </h3>
                      {item.project_id && (
                        <p className="text-sm text-gray-600">{item.construction_type}</p>
                      )}
                    </div>
                    <div className="flex-shrink-0">
                      {getStatusBadge(item.status, item.payment_requested_at, item.payment_confirmed_at)}
                    </div>
                  </div>
                  
                  <div className="flex justify-between gap-3 text-xs">
                    {item.project_id ? (
                      <>
                        <div className="truncate">
                          <span className="text-gray-600">계약금액:</span>
                          <span className="ml-1 font-medium">{formatCurrency(item.contract_amount)}</span>
                        </div>
                        <div className="truncate text-right">
                          <span className="text-gray-600">수수료:</span>
                          <span className="ml-1 font-medium text-purple-600">{formatCurrency(item.commission_amount)}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="truncate">
                          <span className="text-gray-600">구분:</span>
                          <span className="ml-1 font-medium">월정액</span>
                        </div>
                        <div className="truncate text-right">
                          <span className="text-gray-600">금액:</span>
                          <span className="ml-1 font-medium text-purple-600">{formatCurrency(item.commission_amount)}</span>
                        </div>
                      </>
                    )}
                  </div>
                  
                  <div className="mt-2 flex justify-between items-center">
                    <div className="text-xs text-gray-500">
                      생성일: {new Date(item.calculated_at).toLocaleDateString()}
                      {item.payment_confirmed_at && (
                        <span className="ml-2">
                          | 납부확인일: {new Date(item.payment_confirmed_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    {item.project_id && (
                      <button
                        onClick={() => navigate(`/projects/${item.project_id}`)}
                        className="flex items-center justify-end gap-1 text-xs text-purple-600 hover:text-purple-700 transition-colors ml-auto"
                        title="프로젝트 보기"
                      >
                        <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                        <span>프로젝트 보기</span>
                      </button>
                    )}
                  </div>
                  
                  {/* 납부 안내 */}
                  {item.status === 'calculated' && !item.payment_requested_at && accountInfo && (
                    <div className="mt-3 p-3 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg text-xs">
                      <p className="font-semibold text-purple-800 mb-2">💳 납부 계좌 안내</p>
                      <div className="space-y-1 text-purple-700">
                        <p>은행: {accountInfo.bank}</p>
                        <p>계좌번호: {accountInfo.account}</p>
                        <p>예금주: {accountInfo.depositor}</p>
                      </div>
                      <p className="mt-2 text-purple-600 font-medium">※ 입금 후 아래 버튼을 눌러주세요</p>
                    </div>
                  )}

                  {/* 관리자 메모 */}
                  {item.contractor_notes && (
                    <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-800">
                      <p className="font-medium">관리자 메모</p>
                      <p>{item.contractor_notes}</p>
                    </div>
                  )}

                  {/* 입금 완료 버튼 */}
                  {item.status === 'calculated' && !item.payment_requested_at && (
                    <button
                      onClick={() => paymentRequestMutation.mutate(item.id)}
                      disabled={paymentRequestMutation.isLoading}
                      className="mt-3 w-full py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white text-sm font-medium rounded-xl hover:from-purple-700 hover:to-purple-800 disabled:opacity-50 transition-all shadow-lg hover:shadow-xl"
                    >
                      {paymentRequestMutation.isLoading ? '처리중...' : '입금 완료'}
                    </button>
                  )}

                  {/* 입금 취소 버튼 */}
                  {item.status === 'calculated' && item.payment_requested_at && !item.payment_confirmed_at && (
                    <button
                      onClick={() => {
                        if (window.confirm('입금 신고를 취소하시겠습니까?')) {
                          paymentCancelMutation.mutate(item.id);
                        }
                      }}
                      disabled={paymentCancelMutation.isLoading}
                      className="mt-3 w-full py-3 bg-gradient-to-r from-red-500 to-red-600 text-white text-sm font-medium rounded-xl hover:from-red-600 hover:to-red-700 disabled:opacity-50 transition-all"
                    >
                      {paymentCancelMutation.isLoading ? '처리중...' : '입금 신고 취소'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              수수료 내역이 없습니다.
            </div>
          )}
        </div>
      </div>

      {/* 수수료 계산 방식 설명 모달 */}
      <CommissionExplanationModal
        isOpen={showExplanationModal}
        onClose={() => {
          setShowExplanationModal(false);
          setSelectedConstructionType(null);
        }}
        constructionType={selectedConstructionType}
      />
    </div>
  );
};

export default ContractorCommission;